import { registrationFormValues } from "@/components/events/eventFormValues";
import { aiEventDetailsUrl } from "@/lib/aiEventDetails";
import { useState } from "react";
import {
  useAiEvents,
  useApproveAiEvent,
  useRejectAiEvent,
  useBulkRejectAiEvents,
  useAiFestivalOptions,
} from "@/hooks/useAiAgent";
import { useCategories } from "@/hooks/useCategories";
import { useVenues } from "@/hooks/useVenues";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/ui/action-button";
import { PillTabs } from "@/components/ui/pill-tabs";
import { FilterBar } from "@/components/ui/filter-bar";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { Badge } from "@/components/ui/badge";
import { CategoryChip } from "@/components/ui/category-chip";
import { Checkbox } from "@/components/ui/checkbox";
import { FormDialog } from "@/components/ui/form-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, X, ExternalLink, Trash2, Link2, Pencil } from "lucide-react";
import { TableCard } from "@/components/ui/table-card";
import { AiPosterPicker, type AiPosterSelection } from "@/components/ai/AiPosterPicker";
import { AiCollectionEvidence } from "@/components/ai/AiCollectionEvidence";
import { aiEventDateLabel } from "@/lib/aiEventDate";
import { EventFormFields } from "@/components/events/EventFormFields";
import { emptyEventFormValues, type EventFormValues } from "@/components/events/eventFormValues";
import { eventDateInput, eventFormDates } from "@/lib/eventDates";
import { toast } from "sonner";
import type { AiEvent } from "@/hooks/useAiAgent";

const STATUS_OPTIONS = [
  { value: "pending_review", label: "Oczekujące" },
  { value: "rejected", label: "Odrzucone" },
];

export default function AiEventsPage() {
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sheetEvent, setSheetEvent] = useState<AiEvent | null>(null);
  const [festivalChoice, setFestivalChoice] = useState("none");
  const [poster, setPoster] = useState<AiPosterSelection | null>(null);
  const [ticketPrice, setTicketPrice] = useState("");
  const [aiPreFilled, setAiPreFilled] = useState<Set<string>>(new Set());

  const limit = 25;
  const { data, isLoading } = useAiEvents({
    status: statusFilter,
    limit,
    offset: page * limit,
  });
  const { data: categories } = useCategories();
  const { data: venues } = useVenues();
  const {
    data: festivals = [],
    isError: festivalError,
    isLoading: festivalsLoading,
  } = useAiFestivalOptions(!!sheetEvent);
  const selectedFestival = festivals.find((f) => `${f.kind}:${f.id}` === festivalChoice);
  const festivalBlocked =
    festivalChoice !== "none" && (!selectedFestival || selectedFestival.status !== "approved");

  const approve = useApproveAiEvent();
  const reject = useRejectAiEvent();
  const bulkReject = useBulkRejectAiEvents();

  // Approve form state — uses the shared EventFormValues shape
  const [approveForm, setApproveForm] = useState<EventFormValues>(emptyEventFormValues());

  const openSheet = (event: AiEvent) => {
    setSheetEvent(event);
    setPoster(null);
    setTicketPrice(event.ticketPrice ?? "");
    setFestivalChoice(event.parentProposalId ? `proposal:${event.parentProposalId}` : "none");
    const metadata = event.collectionMetadata;
    const dateRange =
      metadata?.datePrecision === "date" &&
      !!metadata.start &&
      (!!metadata.end || metadata.isAllDay);
    const withoutHours = event.isAllDay || dateRange;
    const preFilled = new Set<string>();
    const categoryId = event.suggestedCategoryId ?? "";
    const venueId = event.suggestedVenueId ?? "";
    if (categoryId) preFilled.add("categoryId");
    if (venueId) preFilled.add("venueId");
    setAiPreFilled(preFilled);
    setApproveForm({
      title: event.title,
      eventDescription: event.eventDescription,
      date: dateRange ? metadata.start! : eventDateInput(event.date, withoutHours),
      endDate: dateRange ? (metadata.end ?? "") : eventDateInput(event.endDate, withoutHours),
      isAllDay: withoutHours,
      venueId,
      categoryId,
      detailsUrl: aiEventDetailsUrl(event.sourceUrl, metadata?.sourceURLs),
      ...registrationFormValues(event),
      source: event.sourceName,
      sourceUrl: aiEventDetailsUrl(event.sourceUrl, metadata?.sourceURLs),
      isPreview: false,
    });
  };

  const handleFormChange = (patch: Partial<EventFormValues>) =>
    setApproveForm((f) => ({ ...f, ...patch }));

  const handleApprove = async () => {
    if (
      !sheetEvent ||
      !approveForm.categoryId ||
      (!approveForm.venueId && sheetEvent.collectionMetadata?.kind !== "festival")
    ) {
      toast.error("Wybierz kategorię i lokalizację");
      return;
    }
    if (festivalBlocked) {
      toast.error("Wybierz opublikowany festiwal lub usuń przypisanie");
      return;
    }
    let dates: ReturnType<typeof eventFormDates>;
    try {
      dates = eventFormDates(approveForm);
      if (!dates.date) throw new Error("Uzupełnij datę rozpoczęcia");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Niepoprawny termin");
      return;
    }
    const reviewedPrice = ticketPrice.trim() ? Number(ticketPrice) : null;
    if (
      reviewedPrice !== null &&
      (!Number.isFinite(reviewedPrice) || reviewedPrice < 0 || reviewedPrice > 99999999.99)
    ) {
      toast.error("Podaj poprawną cenę biletu");
      return;
    }
    try {
      const result = await approve.mutateAsync({
        id: sheetEvent.id,
        categoryId: approveForm.categoryId,
        ticketPrice: reviewedPrice,
        posterUrl: poster?.url ?? null,
        posterSha256: poster?.sha256,
        venueId: approveForm.venueId || undefined,
        title: approveForm.title,
        eventDescription: approveForm.eventDescription,
        date: dates.date!,
        endDate: dates.endDate,
        parentProposalId: selectedFestival?.kind === "proposal" ? selectedFestival.id : null,
        parentEventId: selectedFestival?.kind === "event" ? selectedFestival.id : null,
        isAllDay: approveForm.isAllDay,
        detailsUrl: approveForm.detailsUrl,
        registrationOptions: approveForm.registrationOptions,
        registrationNotes: approveForm.registrationNotes,
        source: approveForm.source,
        sourceUrl: approveForm.sourceUrl,
      });
      if (result.notificationWarning) {
        toast.warning("Wydarzenie opublikowane, ale nie wszystkie powiadomienia zostały wysłane");
      } else {
        toast.success("Wydarzenie opublikowane");
      }
      setSheetEvent(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd zatwierdzania");
    }
  };

  const handleReject = async (id: string) => {
    try {
      await reject.mutateAsync(id);
      toast.success("Wydarzenie odrzucone");
      if (sheetEvent?.id === id) setSheetEvent(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd odrzucania");
    }
  };

  const handleBulkReject = async () => {
    if (selected.size === 0) return;
    try {
      await bulkReject.mutateAsync(Array.from(selected));
      toast.success(`Odrzucono ${selected.size} wydarzeń`);
      setSelected(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd odrzucania");
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pending = data?.events.filter((e) => e.status === "pending_review") ?? [];
    if (selected.size === pending.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pending.map((e) => e.id)));
    }
  };

  const events = data?.events ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const confidenceBadge = (confidence: number) => {
    const pct = (confidence * 100).toFixed(0);
    if (confidence >= 0.8)
      return (
        <Badge variant="success" title={`Pewność: ${pct}% — wysoka`}>
          {pct}%
        </Badge>
      );
    if (confidence >= 0.5)
      return (
        <Badge variant="warning" title={`Pewność: ${pct}% — średnia`}>
          {pct}%
        </Badge>
      );
    return (
      <Badge variant="danger" title={`Pewność: ${pct}% — niska, wymaga uwagi`}>
        {pct}%
      </Badge>
    );
  };

  return (
    <div>
      <h1 className="sr-only">Agent AI — Znalezione wydarzenia</h1>
      <p className="mb-6 text-pulso-muted">
        Przeglądaj i zatwierdzaj wydarzenia znalezione przez agenta
      </p>

      {/* Filters */}
      <FilterBar>
        <PillTabs
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(0);
          }}
          options={STATUS_OPTIONS}
        />
      </FilterBar>

      {isLoading ? (
        <p className="text-muted-foreground">Ładowanie...</p>
      ) : (
        <>
          <TableCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        selected.size > 0 &&
                        selected.size === events.filter((e) => e.status === "pending_review").length
                      }
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Tytuł</TableHead>
                  <TableHead>Źródło</TableHead>
                  <TableHead>Kategoria</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Lokalizacja</TableHead>
                  <TableHead className="w-[200px]">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-12 text-center text-sm text-muted-foreground"
                    >
                      Brak wydarzeń. Uruchom agenta, aby znaleźć nowe wydarzenia.
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event) => {
                    const suggestedCategory = categories?.find(
                      (c) => c.id === event.suggestedCategoryId,
                    );
                    return (
                      <TableRow key={event.id}>
                        <TableCell>
                          {event.status === "pending_review" && (
                            <Checkbox
                              checked={selected.has(event.id)}
                              onCheckedChange={() => toggleSelect(event.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell
                          className="max-w-[280px] cursor-pointer truncate font-medium hover:text-primary"
                          onClick={() => openSheet(event)}
                        >
                          {event.title}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Badge
                              variant="info"
                              className="max-w-[180px]"
                              title={event.sourceName}
                            >
                              <span className="truncate">{event.sourceName}</span>
                            </Badge>
                            {event.duplicateOfId && (
                              <Badge variant="warning" className="text-xs">
                                <Link2 className="h-3 w-3" />
                                Duplikat
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {suggestedCategory ? (
                            <CategoryChip category={suggestedCategory} />
                          ) : (
                            <span className="text-pulso-muted">—</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {aiEventDateLabel(event)}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                          {event.locationName || "—"}
                        </TableCell>
                        <TableCell>
                          {event.status === "pending_review" && (
                            <div className="flex items-center gap-1.5">
                              <Button
                                size="sm"
                                className="h-8 gap-1.5 bg-pulso-success text-white hover:bg-pulso-success/90"
                                onClick={() => openSheet(event)}
                              >
                                <Check className="h-3.5 w-3.5" strokeWidth={3} /> Zatwierdź
                              </Button>
                              <Button
                                size="icon"
                                variant="destructive"
                                className="h-8 w-8"
                                title="Odrzuć"
                                onClick={() => handleReject(event.id)}
                              >
                                <X className="h-3.5 w-3.5" strokeWidth={3} />
                              </Button>
                              <ActionButton
                                tooltip="Edytuj"
                                className="h-8 w-8"
                                onClick={() => openSheet(event)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </ActionButton>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableCard>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Poprzednia
              </Button>
              <span className="flex items-center text-sm text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Następna
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail / Approve modal */}
      <FormDialog
        open={!!sheetEvent}
        onOpenChange={(open) => !open && setSheetEvent(null)}
        title={sheetEvent?.title ?? ""}
        size="lg"
        subtitle={
          sheetEvent ? (
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="neutral">{sheetEvent.sourceName}</Badge>
              {!sheetEvent.collectionMetadata && confidenceBadge(sheetEvent.confidence)}
              {sheetEvent.sourceUrl && (
                <a
                  href={sheetEvent.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-pulso-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Otwórz źródło
                </a>
              )}
            </span>
          ) : undefined
        }
        footer={
          sheetEvent ? (
            <>
              <Button
                variant="destructive"
                onClick={() => handleReject(sheetEvent.id)}
                disabled={sheetEvent.status !== "pending_review"}
              >
                Odrzuć
              </Button>
              <Button
                onClick={handleApprove}
                disabled={
                  !approveForm.categoryId ||
                  (!approveForm.venueId && sheetEvent.collectionMetadata?.kind !== "festival") ||
                  !approveForm.date ||
                  festivalBlocked ||
                  approve.isPending ||
                  sheetEvent.status !== "pending_review"
                }
              >
                <Check className="mr-2 h-4 w-4" />
                Zatwierdź i opublikuj
              </Button>
            </>
          ) : undefined
        }
      >
        {sheetEvent && (
          <EventFormFields
            unifiedEventLink
            values={approveForm}
            onChange={handleFormChange}
            categories={categories ?? []}
            venues={venues ?? []}
            aiPreFilled={aiPreFilled}
            aboveDescription={
              <div className="space-y-3">
                <AiCollectionEvidence event={sheetEvent} />
                <AiPosterPicker
                  key={sheetEvent.id}
                  event={sheetEvent}
                  selected={poster}
                  onSelect={setPoster}
                  disabled={approve.isPending || sheetEvent.status !== "pending_review"}
                />
                {sheetEvent.collectionMetadata?.kind !== "festival" && (
                  <div className="space-y-2">
                    <Label htmlFor="ai-festival">Festiwal</Label>
                    <select
                      id="ai-festival"
                      className="w-full rounded-md border bg-background px-3 py-2"
                      value={festivalChoice}
                      onChange={(e) => setFestivalChoice(e.target.value)}
                    >
                      <option value="none">Bez przypisania do festiwalu</option>
                      {festivalChoice !== "none" && !selectedFestival && (
                        <option value={festivalChoice} disabled>
                          Przypisany festiwal — {festivalsLoading ? "ładowanie…" : "niedostępny"}
                        </option>
                      )}
                      {festivals
                        .filter((f) => f.id !== sheetEvent.id)
                        .map((f) => (
                          <option
                            key={`${f.kind}:${f.id}`}
                            value={`${f.kind}:${f.id}`}
                            disabled={f.status === "rejected"}
                          >
                            {f.title} —{" "}
                            {f.status === "approved"
                              ? "opublikowany"
                              : f.status === "rejected"
                                ? "odrzucony"
                                : "oczekuje na publikację"}
                            {f.kind === "proposal" ? " (propozycja AI)" : ""}
                          </option>
                        ))}
                    </select>
                    {selectedFestival && (
                      <p>
                        Część festiwalu: <strong>{selectedFestival.title}</strong>
                      </p>
                    )}
                    {selectedFestival?.status === "pending_review" && (
                      <p>
                        Najpierw opublikuj festiwal „{selectedFestival.title}”. Potem zatwierdź ten
                        wpis.
                      </p>
                    )}
                    {festivalError && (
                      <p role="alert">
                        Nie udało się pobrać listy festiwali. Sprawdź połączenie z API.
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Sprawdź przypisanie. Możesz wybrać inny festiwal lub pozostawić wydarzenie
                      samodzielne.
                    </p>
                  </div>
                )}
                {sheetEvent.locationName && (
                  <div className="rounded-md border border-dashed border-muted-foreground/30 px-3 py-2 text-xs text-muted-foreground">
                    <span className="font-medium">Lokalizacja (z AI): </span>
                    {sheetEvent.locationName}
                    {sheetEvent.locationAddress ? `, ${sheetEvent.locationAddress}` : ""}
                  </div>
                )}
              </div>
            }
            afterFields={
              <div className="space-y-2">
                <Label htmlFor="ai-ticket-price">Cena biletu (zł)</Label>
                <Input
                  id="ai-ticket-price"
                  type="number"
                  min="0"
                  max="99999999.99"
                  step="0.01"
                  value={ticketPrice}
                  onChange={(event) => setTicketPrice(event.target.value)}
                  placeholder="Nieustalona"
                />
                <p className="text-xs text-muted-foreground">
                  Uzupełnij, jeśli źródło podaje jedną cenę. Przedziały cen i warunki opisz w treści
                  wydarzenia.
                </p>
              </div>
            }
          />
        )}
      </FormDialog>

      <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
        <button
          type="button"
          className="bulk-action-btn"
          data-tone="danger"
          disabled={bulkReject.isPending}
          onClick={handleBulkReject}
        >
          <Trash2 className="h-3.5 w-3.5" /> Odrzuć zaznaczone
        </button>
      </BulkActionBar>
    </div>
  );
}
